import { useEffect, useState } from "react"
import { Alert, Box } from "@mui/material"
import { useSearchParams } from "react-router-dom"

import PageHeader from "../components/PageHeader"
import InvoiceTable from "./billing/InvoiceTable"
import AutopayCard from "./billing/AutopayCard"
import { usePermissions } from "../permissions"

/**
 * A client's own invoices, and the card they have authorised.
 *
 * The same table the back office reads, with the controls it does not get: no
 * drafts, no issue, no void — just what was billed, what it was for, and a
 * button that opens Stripe's hosted page to pay it. Card details never touch
 * this app, which is the point of using the hosted page rather than embedding a
 * form.
 *
 * The autopay card is here rather than in Settings because this is the screen
 * somebody is on when they think about paying. Settings is where you go to
 * change something you already know about.
 */
export default function InvoicesPage() {
  const { isBackOffice, organizationLabel } = usePermissions()
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)
  const [params, setParams] = useSearchParams()

  /*
   * Coming back from Stripe's card form.
   *
   * The parameter says only which button they pressed there — the authorisation
   * itself arrives by webhook, and this banner deliberately does not claim the
   * card is saved. The card below reads the real state a moment later and is the
   * thing that says so.
   */
  useEffect(() => {
    const autopay = params.get("autopay")
    if (!autopay) return
    if (autopay === "authorized") setNotice("Thanks — checking with Stripe. Your card appears below once it confirms.")
    if (autopay === "cancelled") setNotice("No card was saved. Nothing has changed.")
    params.delete("autopay")
    setParams(params, { replace: true })
  }, [params, setParams])

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader title="Invoices" subtitle="What you have been billed, and what it was for." />
      {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert> : null}
      {notice ? <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert> : null}

      {/* A client authorises their own card. The back office reaches the same
          control per client from the Billing screen, where it belongs beside the
          charge it pays for. */}
      {!isBackOffice ? <AutopayCard clientLabel={organizationLabel} sx={{ mb: 2 }} /> : null}

      <InvoiceTable onError={setError} onNotice={setNotice} />
    </Box>
  )
}
